// Piano pitches and key signatures, shared by the score editor, renderer, playback and
// the server. Pitches are strings like "C4", "F#5" or "Bb3"; a piano part has two voices,
// the right hand (treble staff) and the left hand (bass staff).

export const RIGHT_HAND = 0, LEFT_HAND = 1;
export const PIANO_LOW = 21, PIANO_HIGH = 108; // A0 to C8
// Where entry starts in each hand before any note is written.
export const HAND_HOME = ["C5", "C3"];

const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const NATURAL = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const SHARPS = ["F", "C", "G", "D", "A", "E", "B"], FLATS = ["B", "E", "A", "D", "G", "C", "F"];
// VexFlow key signature names with their sharps (+) or flats (-), and how the editor lists them.
export const KEY_SIGNATURES = [
  ["Cb", -7, "C♭ major / A♭ minor"], ["Gb", -6, "G♭ major / E♭ minor"], ["Db", -5, "D♭ major / B♭ minor"],
  ["Ab", -4, "A♭ major / F minor"], ["Eb", -3, "E♭ major / C minor"], ["Bb", -2, "B♭ major / G minor"], ["F", -1, "F major / D minor"],
  ["C", 0, "C major / A minor"], ["G", 1, "G major / E minor"], ["D", 2, "D major / B minor"], ["A", 3, "A major / F♯ minor"],
  ["E", 4, "E major / C♯ minor"], ["B", 5, "B major / G♯ minor"], ["F#", 6, "F♯ major / D♯ minor"], ["C#", 7, "C♯ major / A♯ minor"],
].map(([spec, count, label]) => ({ spec, count, label }));
export const KEY_SPECS = KEY_SIGNATURES.map((key) => key.spec);

// The accidental each letter takes in a key, e.g. { F: "#" } for G major.
export function keyAccidentals(spec = "C") {
  const count = KEY_SIGNATURES.find((key) => key.spec === spec)?.count || 0;
  return Object.fromEntries((count > 0 ? SHARPS : FLATS).slice(0, Math.abs(count)).map((letter) => [letter, count > 0 ? "#" : "b"]));
}
const prefersFlats = (spec) => (KEY_SIGNATURES.find((key) => key.spec === spec)?.count || 0) < 0;

export function parsePitch(pitch) {
  const match = /^([A-G])([#b]?)(-?\d)$/.exec(String(pitch));
  return match ? { letter: match[1], accidental: match[2], octave: Number(match[3]) } : null;
}
export function midi(pitch) {
  const parsed = parsePitch(pitch);
  if (!parsed) return NaN;
  return (parsed.octave + 1) * 12 + NATURAL[parsed.letter] + (parsed.accidental === "#" ? 1 : parsed.accidental === "b" ? -1 : 0);
}
// Spells a MIDI number, with flats in flat keys and sharps otherwise, preferring the key's own notes.
export function pitchFromMidi(number, spec = "C") {
  const inKey = keyAccidentals(spec);
  const candidates = LETTERS.flatMap((letter) => ["", "#", "b"].map((accidental) => ({ letter, accidental })))
    .filter(({ letter, accidental }) => (NATURAL[letter] + (accidental === "#" ? 1 : accidental === "b" ? -1 : 0) + 12) % 12 === ((number % 12) + 12) % 12);
  const rank = ({ letter, accidental }) => (inKey[letter] || "") === accidental ? 0 : !accidental ? 1 : (accidental === "b") === prefersFlats(spec) ? 2 : 3;
  const { letter, accidental } = candidates.sort((a, b) => rank(a) - rank(b))[0];
  // B# and Cb belong to a neighbouring octave number.
  const octave = Math.floor((number - NATURAL[letter] - (accidental === "#" ? 1 : accidental === "b" ? -1 : 0)) / 12) - 1;
  return `${letter}${accidental}${octave}`;
}
const clamp = (number) => Math.min(PIANO_HIGH, Math.max(PIANO_LOW, number));

// The note for a typed letter: spelled as the key has it, in the octave nearest `near`.
export function letterInKey(letter, near, spec = "C") {
  const accidental = keyAccidentals(spec)[letter] || "", target = midi(near);
  const options = [-1, 0, 1].map((shift) => `${letter}${accidental}${parsePitch(near).octave + shift}`);
  const best = options.sort((a, b) => Math.abs(midi(a) - target) - Math.abs(midi(b) - target))[0];
  return midi(best) < PIANO_LOW || midi(best) > PIANO_HIGH ? pitchFromMidi(clamp(midi(best)), spec) : best;
}
// A letter added to a chord goes above the chord's top note.
export function letterAbove(letter, chord, spec = "C") {
  const top = Math.max(...chord.map(midi));
  let pitch = `${letter}${keyAccidentals(spec)[letter] || ""}${Math.floor(top / 12) - 1}`;
  while (midi(pitch) <= top) pitch = pitch.replace(/-?\d$/, (octave) => String(Number(octave) + 1));
  return midi(pitch) > PIANO_HIGH ? null : pitch;
}
export function transpose(pitch, semitones, spec = "C") {
  const number = midi(pitch) + semitones;
  return number < PIANO_LOW || number > PIANO_HIGH ? pitch : pitchFromMidi(number, spec);
}
export const sortPitches = (pitches) => [...new Set(pitches)].sort((a, b) => midi(a) - midi(b));

// A pitch moved by an interval (`steps` letter names and `semitones`), keeping its spelling: B♭ up a
// major 2nd is C, F♯ up a perfect 5th is C♯. Where that would need a double sharp or flat, the key spells it.
export function shiftPitch(pitch, steps, semitones, spec = "C") {
  const parsed = parsePitch(pitch);
  if (!parsed) return pitch;
  const position = parsed.octave * 7 + LETTERS.indexOf(parsed.letter) + steps;
  const letter = LETTERS[((position % 7) + 7) % 7], octave = Math.floor(position / 7);
  const target = midi(pitch) + semitones, off = target - ((octave + 1) * 12 + NATURAL[letter]);
  return Math.abs(off) <= 1 ? `${letter}${off > 0 ? "#" : off < 0 ? "b" : ""}${octave}` : pitchFromMidi(target, spec);
}
// A key moved by an interval: { spec, respelled }. Past six sharps or flats it takes the simpler name
// for the same sound (C♯ major reads as D♭), so its notes need spelling for that key (`respelled`).
export function shiftKey(spec, steps, semitones) {
  const count = KEY_SIGNATURES.find((key) => key.spec === spec)?.count || 0;
  const fifths = KEY_SIGNATURES.find((key) => key.spec === shiftPitch("C4", steps, semitones).replace(/-?\d$/, ""))?.count || 0;
  const moved = count + fifths, simple = moved > 6 ? moved - 12 : moved < -6 ? moved + 12 : moved;
  return { spec: KEY_SIGNATURES.find((key) => key.count === simple).spec, respelled: simple !== moved };
}

// The accidental to print beside each pitch of each note in one staff's measure: "#", "b",
// "n" (natural) or null. An accidental lasts to the end of the measure on that line and
// octave; otherwise each letter follows the key signature.
export function measureAccidentals(notes, spec = "C") {
  const key = keyAccidentals(spec), current = new Map();
  return notes.map((note) => (note.notes || []).map((pitch) => {
    const parsed = parsePitch(pitch);
    if (!parsed) return null;
    const line = `${parsed.letter}${parsed.octave}`;
    const expected = current.has(line) ? current.get(line) : key[parsed.letter] || "";
    current.set(line, parsed.accidental);
    return parsed.accidental === expected ? null : parsed.accidental || "n";
  }));
}
